import React from "react";
import { useParams, useLocation, useNavigate } from "react-router";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import useAuth from "../../hooks/useAuth";
import ProjectProvider from "../../components/project/ProjectProvider";
import ProjectNav from "../../components/project/ProjectNav";
import StorageEnabledProvider from "../../components/project/StorageEnabledProvider";
import ProjectTabs from "./ProjectTabs";
import UnsavedChangesDialog from "../modals/UnsavedChanges/UnsavedChangesDialog";
import useUnsavedChanges from "../../hooks/useUnsavedChanges";
import { fullWidthDialog } from "src/constants/dialog";
import { formikRef } from "./settings/Settings";

export default function ProjectInfoModal({ onClose }) {
  const { projectId } = useParams();
  const { user } = useAuth();
  const { settings } = user.company;
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const initTab =
    settings.receiving_wizard === false ? "settings" : projectId ? "inbound" : "reports";
  const value = searchParams.get("tab") || initTab;
  const hasChargesSummaryOpen = [
    "inboundChargesSummaryId",
    "outboundChargesSummaryId",
    "iwoChargesSummaryId",
    "assetChargesSummaryId",
  ].some(param => searchParams.get(param));
  const isCatalogView = ["catalog", "sku_library", "kitting", "grouped_items"].includes(value);

  const setValue = (newValue) => {
    searchParams.set("tab", newValue);
    navigate({
      search: searchParams.toString(),
    });
  };

  const {
    attemptClose,
    attemptChangeTab,
    confirmDiscard,
    cancelDiscard,
    confirmSave,
    showDialog,
    saving,
  } = useUnsavedChanges({
    onClose,
    isDirtyFn: () => !!formikRef.current?.dirty,
    onSave: async () => {
      const submitFn = formikRef.current?.submitForm;
      submitFn();
    },
    activeTab: value,
    setTab: setValue,
    guardedTab: "settings",
  });

  return (
    <Dialog
      open
      fullWidth
      maxWidth={false}
      onClose={attemptClose}
      disableEnforceFocus={hasChargesSummaryOpen}
      disableAutoFocus={hasChargesSummaryOpen}
      disableRestoreFocus={hasChargesSummaryOpen}
      sx={{
        ...fullWidthDialog,
        ...(hasChargesSummaryOpen ? { zIndex: theme => theme.zIndex.modal - 1 } : {}),
        "& .MuiDialog-paper": {
          overflow: "hidden",
          maxWidth: 1050,
          ...(isCatalogView ? { height: "85vh" } : {}),
        },
      }}
    >
      <DialogContent
        sx={{
          bgcolor: "#f4f6f8",
          ...(isCatalogView
            ? { display: "flex", flexDirection: "column", overflow: "hidden" }
            : {}),
        }}
      >
        <DialogContentText
          component="div"
          sx={
            isCatalogView
              ? { display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }
              : undefined
          }
        >
          <ProjectProvider>
            <StorageEnabledProvider>
              <Box display="flex" justifyContent="space-between">
                <Box width={40} />
                <Typography fontSize={{ xs: 22, md: 26, lg: 26 }} fontWeight={600}>
                  {settings.filter_field2} Profile
                </Typography>
                <IconButton color="textPrimary" onClick={attemptClose}>
                  <CloseIcon />
                </IconButton>
              </Box>
              <Box mt={1.5} bgcolor="white">
                <ProjectNav value={value} onChange={attemptChangeTab} />
              </Box>
              <Box
                mt={2}
                {...(isCatalogView
                  ? { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }
                  : { minHeight: 200 })}
              >
                <ProjectTabs selected={value} onClose={attemptClose} onChange={attemptChangeTab} />
              </Box>
            </StorageEnabledProvider>
          </ProjectProvider>
        </DialogContentText>
      </DialogContent>
      {showDialog && (
        <UnsavedChangesDialog
          open={showDialog}
          onCancel={cancelDiscard}
          onDiscard={confirmDiscard}
          onSave={confirmSave}
          saving={saving}
        />
      )}
    </Dialog>
  );
}
